import { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceArea,
  type TooltipContentProps,
} from "recharts";
import type { CompanyCpaTargets, CompanyDailyPoint } from "./types";
import type { Period } from "../../funnel-detail/types";
import { getCompanyFinanceSummary } from "../../../services/financeService";
import { buildSmartYAxisDomain, parseChartDate, resolvePeriodDayDate } from "./chartTime";
import { formatCurrencyCompact, formatCurrency } from "./utils";

type Props = {
  dailyData: CompanyDailyPoint[];
  cpaTargets: CompanyCpaTargets;
  period: Period;
};

type CpaBucketPoint = {
  x: number;
  label: string;
  tooltipLabel: string;
  realCpa: number | null;
  targetCpa: number | null;
  maxAllowedCpa: number | null;
  orders: number;
  spend: number;
  gapToMaxAllowed: number | null;
};

type VisibleChartPeriod = Period & {
  lockedToLast15: boolean;
};

type FixedTimeBucket<T> = {
  x: number;
  label: string;
  tooltipLabel: string;
  rows: T[];
};

const VISIBLE_PERIODS = 15;
const SHORT_MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export function CpaChart({ dailyData, cpaTargets, period }: Props) {
  const [expandedRange, setExpandedRange] = useState<{ dailyData: CompanyDailyPoint[]; cpaTargets: CompanyCpaTargets } | null>(null);
  const chartPeriod = useMemo(() => resolveVisibleChartPeriod(period), [period]);
  const chartDailyData = expandedRange?.dailyData ?? dailyData;
  const chartCpaTargets = expandedRange?.cpaTargets ?? cpaTargets;
  const chartData = useMemo(
    () => buildCpaBuckets(chartDailyData, chartCpaTargets, chartPeriod),
    [chartCpaTargets, chartDailyData, chartPeriod]
  );
  const labelsByX = useMemo(() => new Map(chartData.map((row) => [row.x, row.label])), [chartData]);
  const ticks = useMemo(() => chartData.map((row) => row.x), [chartData]);
  const yDomain = useMemo(
    () =>
      buildSmartYAxisDomain(
        chartData.flatMap((row) => [row.realCpa ?? 0, row.targetCpa ?? 0, row.maxAllowedCpa ?? 0]).filter((value) => value > 0)
      ),
    [chartData]
  );
  const summary = useMemo(() => buildSummary(chartData, chartDailyData, chartCpaTargets), [chartCpaTargets, chartDailyData, chartData]);
  const overAllowedCount = chartData.filter(
    (row) => row.realCpa !== null && row.maxAllowedCpa !== null && row.realCpa > row.maxAllowedCpa
  ).length;
  const hasChartSignal = chartData.some((row) => row.realCpa !== null || row.targetCpa !== null || row.maxAllowedCpa !== null);
  const xDomain = [1, VISIBLE_PERIODS];
  const renderCpaTooltip = (props: TooltipContentProps) => <CpaTooltip {...props} />;

  useEffect(() => {
    let cancelled = false;

    if (!chartPeriod.lockedToLast15) {
      setExpandedRange(null);
      return;
    }

    getCompanyFinanceSummary(chartPeriod)
      .then((summary) => {
        if (!cancelled) {
          setExpandedRange({
            dailyData: summary.dailyData,
            cpaTargets: summary.cpaTargets,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setExpandedRange(null);
      });

    return () => {
      cancelled = true;
    };
  }, [chartPeriod]);

  return (
    <section id="finance-marketing-cpa-trend-target" className="overflow-hidden rounded-lg border border-gray-200 bg-white tabular-nums shadow-sm">
      <header className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">CPA en el tiempo</h2>
            {overAllowedCount > 0 ? (
              <span className="rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700">
                {overAllowedCount} sobre máximo
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-500">CPA real contra objetivo y CPA máximo permitido.</p>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-right">
          <SummaryPill label="CPA real" value={summary.realCpa} />
          <SummaryPill label="Objetivo" value={summary.targetCpa} />
          <SummaryPill label="Máx. permitido" value={summary.maxAllowedCpa} />
        </div>
      </header>

      <div className="h-[340px] px-3 py-4">
        {!hasChartSignal ? (
          <EmptyState />
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 6, left: 0, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis
              dataKey="x"
              type="number"
              domain={xDomain}
              ticks={ticks}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              tickFormatter={(value) => labelsByX.get(Number(value)) ?? ""}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => formatCurrencyCompact(Number(value))}
              domain={yDomain}
              width={60}
            />

            {summary.maxAllowedCpa > 0 ? (
              <ReferenceArea y1={summary.maxAllowedCpa} y2={yDomain[1]} fill="#fee2e2" fillOpacity={0.26} strokeOpacity={0} />
            ) : null}
            <Tooltip content={renderCpaTooltip} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} iconType="circle" iconSize={8} />

            <Line
              type="linear"
              dataKey="realCpa"
              name="CPA real"
              stroke="#2563eb"
              strokeWidth={2.5}
              dot={{ r: 3, fill: "#2563eb", stroke: "#fff", strokeWidth: 1.5 }}
              activeDot={{ r: 5 }}
              connectNulls={false}
            />
            <Line
              type="linear"
              dataKey="targetCpa"
              name="CPA objetivo"
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={{ r: 2.5, fill: "#10b981", stroke: "#fff", strokeWidth: 1.5 }}
              connectNulls
            />
            <Line
              type="linear"
              dataKey="maxAllowedCpa"
              name="CPA máximo permitido"
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="3 5"
              dot={{ r: 2.5, fill: "#ef4444", stroke: "#fff", strokeWidth: 1.5 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function CpaTooltip({ active, payload, label }: TooltipContentProps) {
  const data = payload?.[0]?.payload as CpaBucketPoint | undefined;
  if (!active || !data) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2.5 text-xs tabular-nums">
      <div className="font-semibold text-gray-900 mb-2">{data.tooltipLabel || label}</div>
      <div className="space-y-1">
        <Row label="CPA real" value={data.realCpa ? formatCurrency(data.realCpa) : "--"} color="text-blue-700 font-semibold" />
        <Row label="CPA objetivo" value={data.targetCpa ? formatCurrency(data.targetCpa) : "--"} color="text-emerald-700" />
        <Row label="Máx. permitido" value={data.maxAllowedCpa ? formatCurrency(data.maxAllowedCpa) : "--"} color="text-red-700" />
        <Row label="Compras" value={`${data.orders}`} color="text-gray-700" />
        <Row label="Pauta" value={formatCurrency(data.spend)} color="text-gray-700" />
        {data.gapToMaxAllowed !== null ? (
          <div className="mt-1.5 border-t border-gray-100 pt-1.5">
            <Row
              label="Brecha"
              value={formatSignedCurrency(data.gapToMaxAllowed)}
              color={data.gapToMaxAllowed >= 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="text-gray-600">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}

function SummaryPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[98px] rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2" title={formatCurrency(value)}>
      <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-gray-900">{formatCurrencyCompact(value)}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid h-[300px] place-items-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
      Sin CPA para el periodo seleccionado.
    </div>
  );
}

function buildCpaBuckets(
  dailyData: CompanyDailyPoint[],
  cpaTargets: CompanyCpaTargets,
  period: VisibleChartPeriod
): CpaBucketPoint[] {
  return buildFixedTimeBuckets({
    period,
    rows: dailyData,
    getDate: (row, index) => parseChartDate(row.date) ?? resolvePeriodDayDate(period, row.day, index),
  })
    .map((bucket) => {
      const rowsWithSignals = bucket.rows.filter(
        (row) =>
          Number(row.cpa ?? 0) > 0 ||
          Number(row.orders ?? 0) > 0 ||
          Number(row.adInvestment ?? 0) > 0 ||
          Number(row.targetCpa20 ?? 0) > 0 ||
          Number(row.breakEvenCpa ?? 0) > 0
      );
      const orders = rowsWithSignals.reduce((sum, row) => sum + Number(row.orders ?? 0), 0);
      const spend = rowsWithSignals.reduce((sum, row) => {
        const rowOrders = Number(row.orders ?? 0);
        const adInvestment = Number(row.adInvestment ?? 0);
        const impliedSpend = Number(row.cpa ?? 0) > 0 && rowOrders > 0 ? Number(row.cpa) * rowOrders : 0;
        return sum + (adInvestment > 0 ? adInvestment : impliedSpend);
      }, 0);
      const rawCpaValues = rowsWithSignals.map((row) => Number(row.cpa ?? 0)).filter((value) => value > 0);
      const realCpa = orders > 0 && spend > 0 ? spend / orders : average(rawCpaValues);
      const targetCpa = average(
        rowsWithSignals.map((row) => Number(row.targetCpa20 ?? 0)).filter((value) => value > 0)
      ) ?? (cpaTargets.targetCpa20 > 0 ? cpaTargets.targetCpa20 : null);
      const maxAllowedCpa = average(
        rowsWithSignals.map((row) => Number(row.breakEvenCpa ?? 0)).filter((value) => value > 0)
      ) ?? (cpaTargets.breakEvenCpa > 0 ? cpaTargets.breakEvenCpa : null);
      const gapToMaxAllowed = realCpa !== null && maxAllowedCpa !== null ? maxAllowedCpa - realCpa : null;

      return {
        x: bucket.x,
        label: bucket.label,
        tooltipLabel: bucket.tooltipLabel,
        realCpa,
        targetCpa,
        maxAllowedCpa,
        orders,
        spend,
        gapToMaxAllowed,
      };
    });
}

function buildSummary(
  chartData: CpaBucketPoint[],
  dailyData: CompanyDailyPoint[],
  cpaTargets: CompanyCpaTargets
) {
  const orders = dailyData.reduce((sum, row) => sum + Number(row.orders ?? 0), 0);
  const spend = dailyData.reduce((sum, row) => {
    const rowOrders = Number(row.orders ?? 0);
    const adInvestment = Number(row.adInvestment ?? 0);
    const impliedSpend = Number(row.cpa ?? 0) > 0 && rowOrders > 0 ? Number(row.cpa) * rowOrders : 0;
    return sum + (adInvestment > 0 ? adInvestment : impliedSpend);
  }, 0);
  const fallbackRealCpa = average(chartData.map((row) => row.realCpa).filter(isNumber));

  return {
    realCpa: cpaTargets.realCpa > 0 ? cpaTargets.realCpa : orders > 0 && spend > 0 ? spend / orders : fallbackRealCpa ?? 0,
    targetCpa: cpaTargets.targetCpa20 > 0 ? cpaTargets.targetCpa20 : average(chartData.map((row) => row.targetCpa).filter(isNumber)) ?? 0,
    maxAllowedCpa:
      cpaTargets.breakEvenCpa > 0 ? cpaTargets.breakEvenCpa : average(chartData.map((row) => row.maxAllowedCpa).filter(isNumber)) ?? 0,
  };
}

function average(values: number[]) {
  const validValues = values.filter((value) => Number.isFinite(value) && value > 0);
  if (validValues.length === 0) return null;
  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
}

function isNumber(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

function formatSignedCurrency(value: number) {
  const sign = value >= 0 ? "+" : "-";
  return `${sign}${formatCurrency(Math.abs(value))}`;
}

function resolveVisibleChartPeriod(period: Period): VisibleChartPeriod {
  const to = startOfDay(period.to);
  const shouldRespectFilter = Math.max(1, period.days || countInclusiveDays(period.from, period.to)) >= VISIBLE_PERIODS;
  const from = shouldRespectFilter ? startOfDay(period.from) : addDays(to, -(VISIBLE_PERIODS - 1));

  return {
    ...period,
    from,
    to,
    days: countInclusiveDays(from, to),
    lockedToLast15: !shouldRespectFilter,
  };
}

function buildFixedTimeBuckets<T>({
  period,
  rows,
  getDate,
}: {
  period: VisibleChartPeriod;
  rows: T[];
  getDate: (row: T, index: number) => Date | string | null | undefined;
}): FixedTimeBucket<T>[] {
  const from = startOfDay(period.from);
  const to = startOfDay(period.to);
  const totalDays = Math.max(VISIBLE_PERIODS, countInclusiveDays(from, to));
  const rowsByDate = new Map<string, T[]>();

  rows.forEach((row, index) => {
    const date = parseChartDate(getDate(row, index));
    if (!date || date < from || date > to) return;
    const key = toDateKey(date);
    const existing = rowsByDate.get(key) ?? [];
    existing.push(row);
    rowsByDate.set(key, existing);
  });

  return Array.from({ length: VISIBLE_PERIODS }, (_, index) => {
    const startOffset = Math.floor((index * totalDays) / VISIBLE_PERIODS);
    const endOffset = Math.max(startOffset, Math.floor(((index + 1) * totalDays) / VISIBLE_PERIODS) - 1);
    const startDate = addDays(from, startOffset);
    const endDate = new Date(Math.min(addDays(from, endOffset).getTime(), to.getTime()));
    const bucketRows: T[] = [];

    for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
      bucketRows.push(...(rowsByDate.get(toDateKey(date)) ?? []));
    }

    return {
      x: index + 1,
      label: formatShortDate(startDate),
      tooltipLabel: formatBucketTooltipLabel(startDate, endDate),
      rows: bucketRows,
    };
  });
}

function startOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(value: Date, days: number) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return startOfDay(date);
}

function countInclusiveDays(from: Date, to: Date) {
  const start = startOfDay(from).getTime();
  const end = startOfDay(to).getTime();
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
}

function toDateKey(value: Date) {
  const date = startOfDay(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatShortDate(date: Date) {
  return `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`;
}

function formatBucketTooltipLabel(startDate: Date, endDate: Date) {
  if (toDateKey(startDate) === toDateKey(endDate)) return formatShortDate(startDate);

  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  if (sameMonth) return `${startDate.getDate()}-${endDate.getDate()} ${SHORT_MONTHS[endDate.getMonth()]}`;

  return `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`;
}
